import React, { useEffect, useState, useCallback } from "react";
import {
  Grid,
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import API from "../../utils/Axios.utils";
import useAuth from "../../hooks/useAuth";
import PipelineFunnel from "./PipelineFunnel";
import SupplierScorecards from "./SupplierScorecards";
import AgingJobsAlerts from "./AgingJobsAlerts";
import PredictiveTimeToHire from "./PredictiveTimeToHire";

const KPI_CARDS = [
  {
    key: "avgTimeToFill",
    label: "AVG TIME-TO-FILL",
    unit: "Days",
    icon: AccessTimeIcon,
    iconBg: "#EBF5FF",
    iconColor: "#2563EB",
  },
  {
    key: "avgTimeToInterview",
    label: "TIME-TO-INTERVIEW",
    unit: "Days",
    icon: HourglassEmptyIcon,
    iconBg: "#F0FDF4",
    iconColor: "#16A34A",
  },
  {
    key: "offerAcceptanceRate",
    label: "OFFER ACCEPTANCE",
    unit: "%",
    icon: CheckCircleOutlineIcon,
    iconBg: "#F5F3FF",
    iconColor: "#7C3AED",
  },
];

const SELECT_SX = {
  backgroundColor: "#fff",
  minWidth: 180,
  fontSize: "14px",
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
};

const buildQueryString = (range, skillId, clientId, recruiterId, supplierId) => {
  const params = new URLSearchParams();
  if (range && range !== "all") {
    const dateFrom = new Date();
    dateFrom.setDate(dateFrom.getDate() - parseInt(range));
    params.set("dateFrom", dateFrom.toISOString().split("T")[0]);
  }
  if (skillId) {
    params.set("coreSkillId", skillId);
  }
  if (clientId) {
    params.set("clientId", clientId);
  }
  if (recruiterId) {
    params.set("recruiterId", recruiterId);
  }
  if (supplierId) {
    params.set("supplierId", supplierId);
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
};

const AnalyticsDashboard = () => {
  const { token } = useAuth();

  const [dateRange, setDateRange] = useState("30");
  const [filterCoreSkill, setFilterCoreSkill] = useState("");
  const [coreSkills, setCoreSkills] = useState([]);

  // New filter states
  const [filterClient, setFilterClient] = useState("");
  const [filterRecruiter, setFilterRecruiter] = useState("");
  const [filterSupplier, setFilterSupplier] = useState("");

  // Dropdown option lists
  const [clients, setClients] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const [kpisLoading, setKpisLoading] = useState(true);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [scorecardsLoading, setScorecardsLoading] = useState(true);
  const [agingLoading, setAgingLoading] = useState(true);
  const [predictiveLoading, setPredictiveLoading] = useState(true);
  const [genderLoading, setGenderLoading] = useState(true);

  const [kpis, setKpis] = useState({ avgTimeToFill: 0, avgTimeToInterview: 0, offerAcceptanceRate: 0 });
  const [funnel, setFunnel] = useState(null);
  const [scorecards, setScorecards] = useState([]);
  const [agingJobs, setAgingJobs] = useState([]);
  const [predictiveData, setPredictiveData] = useState([]);
  const [genderData, setGenderData] = useState([]);

  const authConfig = { headers: { Authorization: `Bearer ${token}` } };

  // Fetch core skills + dropdown options on mount
  useEffect(() => {
    API.get("api/core-skill/coreSkills", authConfig)
      .then((res) => setCoreSkills(res.data?.coreSkills || []))
      .catch((err) => console.error("Error fetching core skills:", err));

    // Fetch clients for dropdown
    API.get("api/client/active_client_list", authConfig)
      .then((res) => setClients(res.data?.clients || res.data || []))
      .catch((err) => console.error("Error fetching clients:", err));

    // Fetch recruiters for dropdown
    API.get("api/job/recruiter-list", authConfig)
      .then((res) => setRecruiters(res.data?.recruiters || []))
      .catch((err) => console.error("Error fetching recruiters:", err));

    // Fetch suppliers for dropdown
    API.get("api/users/getallsupplier", authConfig)
      .then((res) => setSuppliers(res.data?.data?.supplier || res.data?.supplier || []))
      .catch((err) => console.error("Error fetching suppliers:", err));
  }, []);

  const fetchAll = useCallback(async () => {
    const qs = buildQueryString(dateRange, filterCoreSkill, filterClient, filterRecruiter, filterSupplier);
    // Aging and Predictive: build their own query strings
    const agingParams = new URLSearchParams();
    if (dateRange && dateRange !== "all") {
      const dateFrom = new Date();
      dateFrom.setDate(dateFrom.getDate() - parseInt(dateRange));
      agingParams.set("dateFrom", dateFrom.toISOString().split("T")[0]);
    }
    if (filterCoreSkill) agingParams.set("coreSkillId", filterCoreSkill);
    if (filterClient) agingParams.set("clientId", filterClient);
    if (filterRecruiter) agingParams.set("recruiterId", filterRecruiter);
    if (filterSupplier) agingParams.set("supplierId", filterSupplier);
    const agingQs = agingParams.toString() ? `?${agingParams.toString()}` : "";

    setKpisLoading(true);
    setFunnelLoading(true);
    setScorecardsLoading(true);
    setAgingLoading(true);
    setPredictiveLoading(true);
    setGenderLoading(true);

    await Promise.allSettled([
      API.get(`/api/dash/analytics/kpis${qs}`, authConfig)
        .then((res) => setKpis({
          avgTimeToFill: res.data?.avgTimeToFill ?? 0,
          avgTimeToInterview: res.data?.avgTimeToInterview ?? 0,
          offerAcceptanceRate: res.data?.offerAcceptanceRate ?? 0,
          trends: res.data?.trends ?? {},
        }))
        .catch((err) => console.error("Error fetching KPIs:", err))
        .finally(() => setKpisLoading(false)),

      API.get(`/api/dash/analytics/funnel${qs}`, authConfig)
        .then((res) => setFunnel(res.data))
        .catch((err) => console.error("Error fetching funnel:", err))
        .finally(() => setFunnelLoading(false)),

      API.get(`/api/dash/analytics/scorecards${qs}`, authConfig)
        .then((res) => setScorecards(res.data || []))
        .catch((err) => console.error("Error fetching scorecards:", err))
        .finally(() => setScorecardsLoading(false)),

      API.get(`/api/dash/analytics/aging${agingQs}`, authConfig)
        .then((res) => setAgingJobs(res.data || []))
        .catch((err) => console.error("Error fetching aging jobs:", err))
        .finally(() => setAgingLoading(false)),

      API.get(`/api/dash/analytics/predictive-time-to-hire${qs}`, authConfig)
        .then((res) => setPredictiveData(res.data || []))
        .catch((err) => console.error("Error fetching predictive data:", err))
        .finally(() => setPredictiveLoading(false)),

      API.get(`/api/dash/analytics/gender${qs}`, authConfig)
        .then((res) => setGenderData(res.data || []))
        .catch((err) => console.error("Error fetching gender data:", err))
        .finally(() => setGenderLoading(false)),
    ]);
  }, [dateRange, filterCoreSkill, filterClient, filterRecruiter, filterSupplier, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Dynamic badge based on actual KPI trend %
  const getBadgeForKPI = (key, kpisObj) => {
    if (key === "offerAcceptanceRate") {
      return null; // Remove badge entirely for Offer Acceptance
    }

    const trend = kpisObj?.trends?.[key];
    if (trend === null || trend === undefined) {
      return null; // Suppress badge entirely when prior period is zero
    }
    
    let text = trend > 0 ? `+${trend}%` : `${trend}%`;
    let bgcolor = "";
    let color = "";

    if (trend === 0) {
      return { text: "0%", bgcolor: "#F1F5F9", color: "#64748B" }; // Neutral gray for 0%
    }
    
    if (key === "avgTimeToFill" || key === "avgTimeToInterview") {
      // For time, lower is better (negative trend is green, positive is red)
      if (trend < 0) { bgcolor = "#DCFCE7"; color = "#16A34A"; } // Green
      else { bgcolor = "#FEE2E2"; color = "#DC2626"; } // Red
    }
    
    return { text, bgcolor, color };
  };

  return (
    <Box sx={{ p: 2.5, mb: 8 }}>

      {/* ── Dashboard Header ── */}
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #E2E8F0",
          borderRadius: 2,
          p: 2.5,
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant="h5" fontWeight={700} color="text.primary">
              Analytics &amp; Reports
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              Comprehensive view of recruitment metrics, supplier performance, and pipeline health.
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            size="small"
            sx={SELECT_SX}
          >
            <MenuItem value="30">Last 30 Days</MenuItem>
            <MenuItem value="60">Last 60 Days</MenuItem>
            <MenuItem value="90">Last 90 Days</MenuItem>
            <MenuItem value="all">All Time</MenuItem>
          </Select>

          <Select
            value={filterCoreSkill}
            onChange={(e) => setFilterCoreSkill(e.target.value)}
            displayEmpty
            size="small"
            sx={SELECT_SX}
            renderValue={(v) =>
              v === ""
                ? "All Core Skills"
                : coreSkills.find((c) => c.id === v)?.name || "All Core Skills"
            }
          >
            <MenuItem value="">All Core Skills</MenuItem>
            {coreSkills.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>

          {/* Client Filter */}
          <Select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            displayEmpty
            size="small"
            sx={SELECT_SX}
            renderValue={(v) =>
              v === ""
                ? "All Clients"
                : clients.find((c) => String(c.client_id) === String(v))?.name || "All Clients"
            }
          >
            <MenuItem value="">All Clients</MenuItem>
            {clients.map((c) => (
              <MenuItem key={c.client_id} value={c.client_id}>
                {c.name}
              </MenuItem>
            ))}
          </Select>

          {/* Recruiter / Admin Filter */}
          <Select
            value={filterRecruiter}
            onChange={(e) => setFilterRecruiter(e.target.value)}
            displayEmpty
            size="small"
            sx={SELECT_SX}
            renderValue={(v) =>
              v === ""
                ? "All Recruiters"
                : recruiters.find((r) => String(r.recruiter_id) === String(v))?.name || "All Recruiters"
            }
          >
            <MenuItem value="">All Recruiters</MenuItem>
            {recruiters.map((r) => (
              <MenuItem key={r.recruiter_id} value={r.recruiter_id}>
                {r.name}
              </MenuItem>
            ))}
          </Select>

          {/* Supplier Filter */}
          <Select
            value={filterSupplier}
            onChange={(e) => setFilterSupplier(e.target.value)}
            displayEmpty
            size="small"
            sx={SELECT_SX}
            renderValue={(v) =>
              v === ""
                ? "All Suppliers"
                : suppliers.find((s) => String(s.supplier_id) === String(v))?.supplier_org_name || "All Suppliers"
            }
          >
            <MenuItem value="">All Suppliers</MenuItem>
            {suppliers.map((s) => (
              <MenuItem key={s.supplier_id} value={s.supplier_id}>
                {s.supplier_org_name || `${s.supplier_admin_name || ""}`}
              </MenuItem>
            ))}
          </Select>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setDateRange("30");
              setFilterCoreSkill("");
              setFilterClient("");
              setFilterRecruiter("");
              setFilterSupplier("");
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* ── KPI Cards ── */}
      <Grid container spacing={2}>
        {KPI_CARDS.map((card) => {
          const Icon = card.icon;
          const value = kpis[card.key];
          const badge = getBadgeForKPI(card.key, kpis);
          return (
            <Grid key={card.key} item xl={3} lg={3} md={6} sm={12} xs={12}>
              <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, p: 2, height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
                  <Box sx={{ bgcolor: card.iconBg, borderRadius: "50%", p: 0.75, display: "flex" }}>
                    <Icon sx={{ color: card.iconColor, fontSize: 20 }} />
                  </Box>
                  {badge && (
                    <Chip
                      label={badge.text}
                      size="small"
                      sx={{ bgcolor: badge.bgcolor, color: badge.color, fontWeight: 700, fontSize: "11px" }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, display: "block", mb: 0.5 }}
                >
                  {card.label}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
                  {kpisLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <>
                      <Typography variant="h4" fontWeight={700} color="text.primary" lineHeight={1.2}>
                        {value}
                      </Typography>
                      <Typography variant="h6" color="text.secondary" fontWeight={400}>
                        {card.unit}
                      </Typography>
                    </>
                  )}
                </Box>
              </Paper>
            </Grid>
          );
        })}

        {/* Demographics KPI Card */}
        <Grid item xl={3} lg={3} md={6} sm={12} xs={12}>
          <Paper elevation={0} sx={{ border: "1px solid #E2E8F0", borderRadius: 2, p: 2, height: "100%" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.5 }}>
              <Box sx={{ bgcolor: "#FFF7ED", borderRadius: "50%", p: 0.75, display: "flex" }}>
                <PeopleOutlinedIcon sx={{ color: "#EA580C", fontSize: 20 }} />
              </Box>
              <Chip
                label="Demographics"
                size="small"
                sx={{ bgcolor: "#FEF3C7", color: "#B45309", fontWeight: 700, fontSize: "11px" }}
              />
            </Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.07em", fontWeight: 600, display: "block", mb: 1 }}
            >
              GENDER BREAKDOWN
            </Typography>
            {genderLoading ? (
              <Box display="flex" alignItems="center" height={60}>
                <CircularProgress size={24} />
              </Box>
            ) : genderData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No data yet</Typography>
            ) : (
              <Box display="flex" flexDirection="column" gap={0.75}>
                {(() => {
                  const total = genderData.reduce((s, d) => s + d.count, 0);
                  const DOT_COLORS = { Male: "#2563EB", Female: "#E11D48", "Prefer not to identify": "#9333EA" };
                  return genderData.map((d) => {
                    const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
                    const color = DOT_COLORS[d.gender] || "#6B7280";
                    return (
                      <Box key={d.gender} display="flex" alignItems="center" gap={1}>
                        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color, flexShrink: 0 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ flex: 1, fontSize: "0.75rem" }}>
                          {d.gender}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ fontSize: "0.8rem" }}>
                          {d.count}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32, textAlign: "right" }}>
                          {pct}%
                        </Typography>
                      </Box>
                    );
                  });
                })()}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>


      {/* ── Visuals Row — Pipeline Funnel + Predictive Time-to-Hire ── */}
      <Box sx={{ mt: 6 }}>
        <Grid container spacing={3}>
          <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
            <PipelineFunnel data={funnel} loading={funnelLoading} />
          </Grid>
          <Grid item xl={6} lg={6} md={12} sm={12} xs={12}>
            <PredictiveTimeToHire data={predictiveData} loading={predictiveLoading} />
          </Grid>
        </Grid>
      </Box>

      {/* ── Tables Row — Supplier Scorecards + Aging Jobs ── */}
      <Box sx={{ mt: 8 }}>
        <Grid container spacing={3}>
          <Grid item xl={7} lg={7} md={12} sm={12} xs={12}>
            <SupplierScorecards data={scorecards} loading={scorecardsLoading} />
          </Grid>
          <Grid item xl={5} lg={5} md={12} sm={12} xs={12}>
            <AgingJobsAlerts data={agingJobs} loading={agingLoading} />
          </Grid>
        </Grid>
      </Box>

    </Box>
  );
};

export default AnalyticsDashboard;
